import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  type AuthUser,
} from 'aws-amplify/auth';
import { environment } from '../../../environments/environment';

interface TokenProfile {
  givenName: string;
  familyName: string;
  email: string;
  groups: string[];
}

const EMPTY_PROFILE: TokenProfile = { givenName: '', familyName: '', email: '', groups: [] };

@Injectable({ providedIn: 'root' })
export class AdminAuthService {
  private readonly userSubject = new BehaviorSubject<AuthUser | null>(null);
  private readonly profileSubject = new BehaviorSubject<TokenProfile>(EMPTY_PROFILE);

  readonly currentUser$: Observable<AuthUser | null> = this.userSubject.asObservable();
  readonly isAuthenticated$: Observable<boolean> = this.userSubject.pipe(
    map((user) => user !== null),
  );

  // Display name: "Given Family", falling back to email, then "Admin"
  readonly displayName$: Observable<string> = this.profileSubject.pipe(
    map(p => {
      if (p.givenName && p.familyName) return `${p.givenName} ${p.familyName}`;
      if (p.givenName) return p.givenName;
      return p.email || 'Admin';
    }),
  );

  // Two-letter initials derived from name or email
  readonly userInitials$: Observable<string> = this.profileSubject.pipe(
    map(p => {
      if (p.givenName && p.familyName) {
        return `${p.givenName[0]}${p.familyName[0]}`.toUpperCase();
      }
      if (p.givenName) return p.givenName.slice(0, 2).toUpperCase();
      return p.email.slice(0, 2).toUpperCase() || 'AD';
    }),
  );

  constructor() {
    this.refreshUser();
  }

  async signIn(username: string, password: string): Promise<void> {
    const { isSignedIn } = await signIn({ username, password });
    if (isSignedIn) {
      this.userSubject.next(await getCurrentUser());
      await this.refreshProfile();
    }
  }

  async signOut(): Promise<void> {
    await signOut();
    this.userSubject.next(null);
    this.profileSubject.next(EMPTY_PROFILE);
  }

  async getIdToken(): Promise<string | null> {
    try {
      const { tokens } = await fetchAuthSession();
      return tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }

  async getGroups(): Promise<string[]> {
    return (await this.decodeToken())?.groups ?? [];
  }

  async isAdmin(): Promise<boolean> {
    return (await this.getGroups()).includes('Admins');
  }

  private async refreshUser(): Promise<void> {
    try {
      this.userSubject.next(await getCurrentUser());
      await this.refreshProfile();
    } catch {
      this.userSubject.next(null);
      this.profileSubject.next(EMPTY_PROFILE);
    }
  }

  private async refreshProfile(): Promise<void> {
    const profile = await this.decodeToken();
    if (profile?.givenName || profile?.familyName) {
      this.profileSubject.next(profile);
      return;
    }
    // JWT has no name claims — look up the DynamoDB record by Cognito sub.
    // Uses fetch() directly to avoid a circular dependency with the HTTP interceptor.
    try {
      const user = await getCurrentUser();
      const token = await this.getIdToken();
      if (token && user.userId) {
        const resp = await fetch(`${environment.apiUrl}/admin/users/${user.userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json() as { givenName?: string; familyName?: string };
          this.profileSubject.next({
            givenName: data.givenName ?? '',
            familyName: data.familyName ?? '',
            email: profile?.email ?? '',
            groups: profile?.groups ?? [],
          });
          return;
        }
      }
    } catch { /* fall through */ }
    this.profileSubject.next(profile ?? EMPTY_PROFILE);
  }

  private async decodeToken(): Promise<TokenProfile | null> {
    const token = await this.getIdToken();
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64)) as Record<string, unknown>;
      return {
        givenName: (payload['given_name'] as string) ?? '',
        familyName: (payload['family_name'] as string) ?? '',
        email: (payload['email'] as string) ?? '',
        groups: Array.isArray(payload['cognito:groups'])
          ? (payload['cognito:groups'] as string[])
          : [],
      };
    } catch {
      return null;
    }
  }
}
